import * as React from 'react';
import { cn } from '@/lib/utils';
import { initials } from '@/lib/chat-model';
import { useAvatarUrl } from '@/lib/avatars';

type Size = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

const SIZES: Record<Size, string> = {
  sm: 'size-7 text-[11px]',
  md: 'size-9 text-xs',
  lg: 'size-11 text-sm',
  xl: 'size-16 text-lg',
  '2xl': 'size-24 text-2xl',
};

const DOT: Record<Size, string> = {
  sm: 'size-2 ring-1',
  md: 'size-2.5 ring-2',
  lg: 'size-3 ring-2',
  xl: 'size-3.5 ring-2',
  '2xl': 'size-4 ring-2',
};

export function Avatar({
  name,
  personId,
  src,
  size = 'md',
  online,
  group = false,
  className,
}: {
  name: string;
  personId?: string;
  src?: string | null;
  size?: Size;
  online?: boolean;
  group?: boolean;
  className?: string;
}): React.ReactElement {
  const looked = useAvatarUrl(src === undefined ? personId : undefined);
  const url = src === undefined ? looked : src;
  const [broken, setBroken] = React.useState<string | null>(null);
  const showImage = url !== null && url !== undefined && broken !== url;
  return (
    <span className={cn('relative inline-flex shrink-0', className)}>
      {showImage ? (
        <img
          src={url}
          alt=""
          onError={() => setBroken(url)}
          className={cn('rounded-full object-cover', SIZES[size])}
        />
      ) : (
        <span
          aria-hidden="true"
          className={cn(
            'inline-flex items-center justify-center rounded-full font-semibold tracking-wide',
            group
              ? 'bg-[hsl(var(--secondary))] text-[hsl(var(--secondary-foreground))]'
              : 'bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]',
            SIZES[size],
          )}
        >
          {group ? '#' : initials(name)}
        </span>
      )}
      {online !== undefined ? (
        <span
          title={online ? 'Online' : 'Offline'}
          className={cn(
            'absolute -bottom-0.5 -right-0.5 rounded-full ring-[hsl(var(--card))]',
            DOT[size],
            online ? 'bg-[hsl(var(--brand))]' : 'bg-[hsl(var(--border))]',
          )}
        />
      ) : null}
    </span>
  );
}
